import { FC, useMemo } from 'react';
import dynamic from 'next/dynamic';
import Skeleton from 'src/shared-components/Loaders/Skeleton';
import { GeneratedLinkUrlObject } from '../../utils/links';

const dots = 'search/dots.svg';

const NextImage = dynamic(
  () => import('src/shared-components/NextImage/NextImage'),
  {
    loading: () => <Skeleton width={21} height={3} />
  }
);

const PageNumber = dynamic(() => import('./PageNumber'), {
  loading: () => <Skeleton width={24} height={24} />
});

interface Props {
  selectedPages: number[];
  lastPageIndex: number;
  getPaginationLink: (index: number) => GeneratedLinkUrlObject;
}

const Pagination: FC<Props> = ({
  getPaginationLink,
  lastPageIndex,
  selectedPages
}) => {
  // Magical number that represent the amount of number to be shown between dots
  const visibleCount = 4;

  const numbersToShow = useMemo((): number[] => {
    const lastItem = selectedPages[0];
    if (lastPageIndex <= visibleCount) {
      const arr = Array.from(Array(lastPageIndex + 1).keys());
      return arr.splice(1, lastPageIndex + 1);
    }
    if (selectedPages?.length >= visibleCount) {
      return selectedPages;
    }
    if (lastItem <= visibleCount) {
      const arr = Array.from(Array(visibleCount + 2).keys());
      return arr.splice(1, visibleCount + 2);
    }
    if (lastItem + visibleCount > lastPageIndex) {
      const arr = Array.from(Array(lastPageIndex + 1).keys());
      return arr.splice(lastPageIndex - visibleCount, visibleCount + 1);
    }

    if (lastItem) {
      const arr = Array.from(Array(lastItem + 3).keys());
      return arr.splice(lastItem - visibleCount + 2, visibleCount + 2);
    }
    return [];
  }, [lastPageIndex, selectedPages]);

  return (
    <div className="flex mt-11 space-x-2">
      {!numbersToShow?.includes(1) && (
        <>
          <PageNumber
            key="firstPageController_1"
            pageIndex={1}
            selectedPages={selectedPages}
            getPaginationLink={getPaginationLink}
          />
          <div className="flex items-end">
            <NextImage
              alt="dots icon"
              layout="fixed"
              height={3}
              width={21}
              src={dots}
            />
          </div>
        </>
      )}
      {numbersToShow.map((value) => {
        return (
          <PageNumber
            key={`middlePageController_${value}`}
            pageIndex={value}
            selectedPages={selectedPages}
            getPaginationLink={getPaginationLink}
          />
        );
      })}
      {!numbersToShow?.includes(lastPageIndex) && (
        <>
          <div className="flex items-end">
            <NextImage
              alt="dots icon"
              layout="fixed"
              height={3}
              width={21}
              src={dots}
            />
          </div>
          <PageNumber
            key={`lastPageController_${lastPageIndex}`}
            pageIndex={lastPageIndex}
            selectedPages={selectedPages}
            getPaginationLink={getPaginationLink}
          />
        </>
      )}
    </div>
  );
};

export default Pagination;
